
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { validateOllamaUrl, fetchOllamaModels } from "@/lib/ollama";
import { Loader2, Check, AlertCircle, Lock } from "lucide-react";
import { useAppStore } from "@/store";

const OllamaSettings = () => {
  const [adminApiUrl, setAdminApiUrl] = useState<string>("");
  const [enterpriseApiUrl, setEnterpriseApiUrl] = useState<string>("");
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const { user } = useAppStore();
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  
  // Check if user is an enterprise tier user
  const isEnterprise = user?.subscription === 'enterprise';

  // Load saved API URLs on component mount
  useEffect(() => {
    const savedAdminApiUrl = localStorage.getItem("ollama_admin_api_url");
    const savedEnterpriseApiUrl = localStorage.getItem("ollama_enterprise_api_url");
    
    if (isAdmin && savedAdminApiUrl) {
      setAdminApiUrl(savedAdminApiUrl);
      checkConnection(savedAdminApiUrl);
    }
    
    if (isEnterprise && savedEnterpriseApiUrl) {
      setEnterpriseApiUrl(savedEnterpriseApiUrl);
      checkConnection(savedEnterpriseApiUrl);
    } else if (isEnterprise && savedAdminApiUrl) {
      // Show admin URL as fallback for enterprise users
      setEnterpriseApiUrl(savedAdminApiUrl);
      checkConnection(savedAdminApiUrl);
    }
  }, [isAdmin, isEnterprise]);

  const checkConnection = async (url: string) => {
    setIsValidating(true);
    setIsValid(null);
    
    try {
      const isValidConnection = await validateOllamaUrl(url);
      setIsValid(isValidConnection);
      
      if (isValidConnection) {
        // Fetch available models
        const models = await fetchOllamaModels();
        setAvailableModels(models);
      }
    } catch (error) {
      console.error("Error validating Ollama URL:", error);
      setIsValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async (isEnterpriseConfig: boolean = false) => {
    const apiUrl = isEnterpriseConfig ? enterpriseApiUrl : adminApiUrl;
    const storageKey = isEnterpriseConfig ? "ollama_enterprise_api_url" : "ollama_admin_api_url";
    
    if (!apiUrl.trim()) {
      localStorage.removeItem(storageKey);
      localStorage.removeItem("ollama_api_url");
    
      if (isEnterpriseConfig) {
        setEnterpriseApiUrl("");
      } else {
        setAdminApiUrl("");
      }
    
      setAvailableModels([]);
      setIsValid(null);
    
      toast({
        title: "Ollama URL Cleared",
        description: `The Ollama API URL has been removed${isEnterpriseConfig ? " for your enterprise" : ""}.`,
      });
    
      return;
    }
    

    setIsValidating(true);
    
    try {
      // Format URL correctly
      let formattedUrl = apiUrl.trim();
      if (!formattedUrl.startsWith("http")) {
        formattedUrl = `http://${formattedUrl}`;
      }
      
      const isValidConnection = await validateOllamaUrl(formattedUrl);
      
      if (isValidConnection) {
        // Save to localStorage with appropriate key
        localStorage.setItem(storageKey, formattedUrl);
        
        // Also save to the legacy key for backward compatibility
        localStorage.setItem("ollama_api_url", formattedUrl);
        
        if (isEnterpriseConfig) {
          setEnterpriseApiUrl(formattedUrl);
        } else {
          setAdminApiUrl(formattedUrl);
        }
        
        setIsValid(true);
        
        // Fetch available models
        const models = await fetchOllamaModels();
        setAvailableModels(models);
        
        toast({
          title: "Success",
          description: `Ollama API URL saved successfully${isEnterpriseConfig ? " for your enterprise" : ""}`,
        });
      } else {
        setIsValid(false);
        toast({
          title: "Connection Failed",
          description: "Could not connect to Ollama API. Please check the URL and make sure Ollama is running.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error saving Ollama API URL:", error);
      setIsValid(false);
      toast({
        title: "Error",
        description: "Failed to connect to Ollama API",
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ollama Settings</CardTitle>
        <CardDescription>
          Configure your Ollama API connection. Ollama runs LLMs locally on your machine.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isAdmin ? (
            <div>
              <label className="text-sm font-medium mb-2 block">
                Global Ollama API URL (Admin only)
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="http://localhost:11434"
                  value={adminApiUrl}
                  onChange={(e) => setAdminApiUrl(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={() => handleSave(false)}
                  disabled={isValidating}
                >
                  {isValidating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                This URL will be used by all non-enterprise users
              </p>
            </div>
          ) : isEnterprise ? (
            <div>
              <label className="text-sm font-medium mb-2 block">
                Enterprise Ollama API URL
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="http://localhost:11434"
                  value={enterpriseApiUrl}
                  onChange={(e) => setEnterpriseApiUrl(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={() => handleSave(true)}
                  disabled={isValidating}
                >
                  {isValidating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                This URL will be used by your enterprise group. If not set, you'll use the admin URL.
              </p>
            </div>
          ) : (
            <div className="rounded-md bg-muted p-3 flex items-start">
              <Lock className="h-5 w-5 mr-2 flex-shrink-0 text-muted-foreground" />
              <div>
                <h4 className="font-medium">Admin-only setting</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  The Ollama API URL can only be configured by administrators or enterprise users.
                </p>
              </div>
            </div>
          )}

          {isValid === true && (
            <div className="rounded-md bg-green-50 dark:bg-green-950 p-3 flex items-start text-green-700 dark:text-green-300">
              <Check className="h-5 w-5 mr-2 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Connected to Ollama</h4>
                <div className="mt-1 text-sm">
                  {availableModels.length > 0 ? (
                    <div>
                      <p className="font-medium">Available models:</p>
                      <ul className="list-disc pl-5 mt-1">
                        {availableModels.slice(0, 5).map((model, index) => {
                          const modelLabel = typeof model === "string" ? model : JSON.stringify(model);
                          return <li key={`model-${index}`}>{modelLabel}</li>;
                        })}
                        {availableModels.length > 5 && (
                          <li>...and {availableModels.length - 5} more</li>
                        )}
                      </ul>

                    </div>
                  ) : (
                    <p>No models found. You may need to pull models using the Ollama CLI.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isValid === false && (
            <div className="rounded-md bg-red-50 dark:bg-red-950 p-3 flex items-start text-red-700 dark:text-red-300">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Connection failed</h4>
                <p className="mt-1 text-sm">
                  Could not connect to Ollama. Please check the URL and ensure Ollama is running.
                </p>
              </div>
            </div>
          )}

          <div className="mt-4 text-sm text-muted-foreground">
            <p className="font-medium">Using Ollama models</p>
            <p className="mt-1">
              All users can select Ollama models from the model dropdown when chatting.
              The Ollama connection is configured by your organization admin.
            </p>
            {!isAdmin && !isEnterprise && (
              <p className="mt-2">
                If you're encountering issues with Ollama, please contact your administrator.
              </p>
            )}
            {(isAdmin || isEnterprise) && (
              <>
                <p className="mt-2">
                  Don't have Ollama? Visit{" "}
                  <a 
                    href="https://ollama.ai" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    ollama.ai
                  </a>{" "}
                  to download and install it on your computer.
                </p>
                <p className="mt-2">
                  After installation, pull a model with:{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">ollama pull llama3</code>
                </p>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OllamaSettings;
